import { getEventDetails } from '@store/slices/eventSlice';
import {
  Image,
  InfoFooterWrapper,
  GridContainer,
  TextWrapper,
  CompanyInfoRoot,
  Underline,
  TitleText,
  Subheadline,
  DescriptionText,
  ButtonsRow,
  PlainLink,
} from './styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import {
  formatWebsiteLink,
  getFullFacebookHttpLink,
  getFullInstagramHttpLink,
  getFullLinkedInHttpLink,
  getFullTwitterHttpLink,
  getFullXingHttpLink,
  getImageSrc,
} from '@utils/fn';
import Icon from '@components/Icon';
import defaultProfileImage from '@assets/images/Default_Profile.png';
import { Company } from '@hubs101/booking-api-skd-client/lib/types';
import Button from '@components/Button';
import DownloadAgenda from '@composites/InboundButton/components/DownloadAgenda';
import { MLSID } from '@utils/pageBuilderHelper';
import { defaultInboundValues } from '@composites/InboundButton/utils';
import { SubtitleWrapper, SocialIcons } from '@composites/AttendeeInfo/styles';

const CompanyInfo = ({
  company,
  backgroundColor,
  injectCompanyDescription = false,
}: {
  company: Company & { youtube?: string };
  backgroundColor?: string;
  injectCompanyDescription?: boolean;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const sluggedEvent = useSelector(getEventDetails)?.[0];

  if (!company) return null;

  const {
    linkedin,
    xing,
    twitter,
    facebook,
    youtube,
    instagram,
    logo,
    name,
    about,
    website1,
  } = company;

  const inboundValues = {
    booleanValues: defaultInboundValues,
    nonBooleanValues: {},
  };

  const socialMediaIcons = [
    linkedin && {
      icon: 'social_linkedin',
      onClick: () => window.open(getFullLinkedInHttpLink(linkedin), '_blank'),
    },
    xing && {
      icon: 'social_xing',
      onClick: () => {
        window.open(getFullXingHttpLink(xing), '_blank');
      },
    },
    twitter && {
      icon: 'social_twitter',
      onClick: () => window.open(getFullTwitterHttpLink(twitter), '_blank'),
    },
    facebook && {
      icon: 'social_facebook',
      onClick: () => window.open(getFullFacebookHttpLink(facebook), '_blank'),
    },
    youtube && {
      icon: 'social_youtube',
      onClick: () => window.open(formatWebsiteLink(youtube), '_blank'),
    },
    instagram && {
      icon: 'social_instagram',
      onClick: () => window.open(getFullInstagramHttpLink(instagram), '_blank'),
    },
  ];

  return (
    <CompanyInfoRoot backgroundColor={backgroundColor}>
      <TitleText color={sluggedEvent?.design?.headerFontColor}>
        {name}
      </TitleText>
      <SubtitleWrapper>
        <SocialIcons>
          {socialMediaIcons
            .filter(item => !!item)
            .map(item => (
              <Icon
                key={item.icon}
                testId={item.icon}
                name={item.icon}
                color={theme.color.black}
                $hoverColor={theme.color.primary}
                $clickable
                onClick={item.onClick}
                indentLess
                size={25}
                $hoverEffect
              />
            ))}
        </SocialIcons>
      </SubtitleWrapper>
      <Underline color={sluggedEvent?.design?.themeColor} />
      <GridContainer>
        <Image src={getImageSrc(logo) || defaultProfileImage} noAspectRatio />

        <TextWrapper>
          <div>
            {/* <Subheadline> {t('Company Description')} </Subheadline> */}
            {injectCompanyDescription ? (
              <DescriptionText dangerouslySetInnerHTML={{ __html: about }} />
            ) : (
              <DescriptionText>{about}</DescriptionText>
            )}
          </div>
          <div>
            {' '}
            <PlainLink
              href={formatWebsiteLink(website1)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {website1}
            </PlainLink>
          </div>
          <InfoFooterWrapper>
            <ButtonsRow>
              <DownloadAgenda
                label={t('agenda-download')}
                buttonType={MLSID.AGENDA_DOWNLOAD}
                inboundValues={inboundValues}
              />

              <Button
                title={t('book-now')}
                backgroundColor={sluggedEvent?.design?.themeColor}
                hoverBackgroundColor={theme.color.mobilityGrey}
                textColor={theme.color.white}
                borderRadius={theme.borderRadius.radius10}
                padding="1.1rem 4.9rem"
                onClick={() => {
                  window.location.href = `${window.location.origin}/events/${sluggedEvent?.slug}${t('book-now-url')}`;
                }}
              />
            </ButtonsRow>
          </InfoFooterWrapper>
        </TextWrapper>
      </GridContainer>
    </CompanyInfoRoot>
  );
};

CompanyInfo.displayName = 'CompanyInfo';

export default CompanyInfo;
